// GitHub service: the free-code pages (/Github, shown at /code) and the GitHub username lookup.
import { githubAPI } from '../api/apiService';
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { seoConfig } from '../configs/seoConfig';
import { getErrorMessage } from '../utils/apiError';

// Link names (slugs) follow the server rules in GithubCodeRequest: 3–80 lowercase
// letters, numbers and single hyphens, not starting or ending with a hyphen.
export const isValidSlug = slug =>
  typeof slug === 'string' &&
  slug.length >= 3 &&
  slug.length <= 80 &&
  /^[a-z0-9-]+$/.test(slug) &&
  !slug.startsWith('-') &&
  !slug.endsWith('-') &&
  !slug.includes('--');

export const RESERVED_SLUGS = [
  'search-user',
  'verify-access',
  'collaborators',
  'is-collaborator',
  'all-collaborators',
  'verify-order-access',
  'add-collaborator',
];

/** The slug the server makes from a title when no link name is given. */
export const slugify = title => {
  const slug = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
  return slug.length >= 3 ? slug : 'code';
};

/** Site path of an entry's page. Older entries without a slug use their id. */
export const codePath = code => `/code/${code?.slug || code?.id}`;

/** Full shareable link for YouTube descriptions, always on the public domain. */
export const codeShareUrl = code => `${seoConfig.siteUrl}${codePath(code)}`;

/** What kind of place a code link points to, for button labels and icons. */
export const codeLinkHost = url => {
  let host = '';
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return { kind: 'other', name: '' };
  }
  if (host === 'github.com') return { kind: 'github', name: 'GitHub' };
  if (host === 'gitlab.com') return { kind: 'git', name: 'GitLab' };
  if (host === 'bitbucket.org') return { kind: 'git', name: 'Bitbucket' };
  if (host === 'drive.google.com' || host === 'docs.google.com')
    return { kind: 'drive', name: 'Google Drive' };
  if (host.endsWith('dropbox.com')) return { kind: 'drive', name: 'Dropbox' };
  if (host === 'mega.nz') return { kind: 'drive', name: 'MEGA' };
  if (host.endsWith('onedrive.live.com') || host === '1drv.ms')
    return { kind: 'drive', name: 'OneDrive' };
  return { kind: 'other', name: host };
};

/**
 * Look up a GitHub user by username.
 * Resolves with { success, data } or { success: false, error }; never throws.
 */
export const fetchGithubUserProfile = async username => {
  if (!username || username.trim() === '') {
    return { success: false, error: 'GitHub username is required', data: null };
  }

  try {
    const response = await apiClient.get(API_ENDPOINTS.GITHUB.SEARCH_USER(username));

    if (response.data?.success && response.data?.data?.user) {
      return { success: true, data: response.data.data.user };
    }

    return {
      success: false,
      error: response.data?.message || 'Failed to fetch GitHub profile',
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error, 'Failed to fetch GitHub user profile'),
      data: null,
    };
  }
};

/**
 * All GitHub code entries. Resolves with { success, data: [] } or { success: false, error, data: [] }.
 * Older API builds answer 404 when the list is empty; that is treated as an empty list.
 */
export const fetchGithubCodes = async () => {
  try {
    const response = await githubAPI.getAll();
    const data = response.data?.data ?? response.data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch (error) {
    if (error?.response?.status === 404) return { success: true, data: [] };
    return {
      success: false,
      error: getErrorMessage(error, 'Failed to fetch GitHub codes'),
      data: [],
    };
  }
};

const isValidUrl = url => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Client-side checks that mirror the server rules. Returns a list of messages (empty when valid). */
export const validateGithubData = data => {
  const errors = [];

  if (!data.title || data.title.trim().length === 0) {
    errors.push('Title is required');
  } else if (data.title.trim().length < 3) {
    errors.push('Title must be at least 3 characters');
  } else if (data.title.length > 200) {
    errors.push('Title must not exceed 200 characters');
  }

  if (!data.description || data.description.trim().length === 0) {
    errors.push('Description is required');
  }

  if (Array.isArray(data.tags) && data.tags.length > 20) {
    errors.push('Maximum 20 tags allowed');
  }

  if (!data.author || data.author.trim().length === 0) {
    errors.push('Author is required');
  }

  if (!data.link || data.link.trim().length === 0) {
    errors.push('Code link is required');
  } else if (!isValidUrl(data.link.trim())) {
    errors.push('Invalid code link URL');
  }

  const slug = (data.slug || '').trim().toLowerCase();
  if (slug && !isValidSlug(slug)) {
    errors.push('Use 3–80 lowercase letters, numbers and hyphens for the link name');
  } else if (RESERVED_SLUGS.includes(slug)) {
    errors.push('That link name is reserved. Choose another');
  }

  if (data.videoLink && !isValidUrl(data.videoLink.trim())) {
    errors.push('Invalid video link URL');
  }

  if (data.demoLink && !isValidUrl(data.demoLink.trim())) {
    errors.push('Invalid demo link URL');
  }

  return errors;
};

const githubBody = data => ({
  title: data.title.trim(),
  description: data.description.trim(),
  tags: data.tags || [],
  author: data.author.trim(),
  link: data.link.trim(),
  videoLink: data.videoLink?.trim() || '',
  demoLink: data.demoLink?.trim() || '',
  slug: data.slug?.trim().toLowerCase() || null,
  requireSignIn: Boolean(data.requireSignIn),
});

/**
 * One entry by slug or id: GET /Github/{key}. The server leaves `link` null and sets
 * `locked` when the entry needs sign-in and the visitor isn't signed in.
 * Resolves with { success, data }, { success: false, notFound: true } or { success: false, error }.
 */
export const fetchGithubCode = async key => {
  try {
    const response = await githubAPI.getByKey(key);
    const data = response.data?.data ?? response.data;
    if (!data || typeof data !== 'object') return { success: false, notFound: true };
    return { success: true, data };
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 404 && status !== 405) {
      return { success: false, error: getErrorMessage(error, "We couldn't load this code link.") };
    }
    // API builds without GET /Github/{key}: find the entry in the full list instead.
    const list = await fetchGithubCodes();
    const match = list.success && list.data.find(code => code.slug === key || code.id === key);
    return match ? { success: true, data: match } : { success: false, notFound: true };
  }
};

/** Create an entry (admin). Resolves with { success, data, message } or { success: false, error }. */
export const createGithubCode = async githubData => {
  const errors = validateGithubData(githubData);
  if (errors.length > 0) {
    return { success: false, error: errors.join(', '), validationErrors: errors };
  }

  try {
    const response = await githubAPI.create(githubBody(githubData));
    return {
      success: true,
      data: response.data?.data || response.data,
      message: 'GitHub code added',
    };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to create GitHub code') };
  }
};

/** Replace an entry's fields (admin): PUT /Github/{id}. */
export const updateGithubCode = async (id, githubData) => {
  const errors = validateGithubData(githubData);
  if (errors.length > 0) {
    return { success: false, error: errors.join(', '), validationErrors: errors };
  }

  try {
    const response = await apiClient.put(API_ENDPOINTS.GITHUB.UPDATE(id), githubBody(githubData));
    return { success: true, data: response.data?.data, message: 'GitHub code updated' };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to update GitHub code') };
  }
};

/** Delete an entry (admin): DELETE /Github/{id}. Only the showcase entry; repositories are untouched. */
export const deleteGithubCode = async id => {
  try {
    await apiClient.delete(API_ENDPOINTS.GITHUB.DELETE(id));
    return { success: true, message: 'GitHub code deleted' };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to delete GitHub code') };
  }
};

/** Adds formattedDate ("4 Oct 2026") and a 150-character shortDescription. */
export const formatGithubCode = code => ({
  ...code,
  formattedDate: code.createdAt
    ? new Date(code.createdAt).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Unknown',
  shortDescription:
    code.description && code.description.length > 150
      ? `${code.description.substring(0, 147)}...`
      : code.description,
});

/** Filter by a search query (title, description, author, tags) and selected tags. */
export const filterGithubCodes = (codes, searchQuery = '', selectedTags = []) => {
  let filtered = [...codes];

  if (searchQuery && searchQuery.trim().length > 0) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(
      code =>
        code.title?.toLowerCase().includes(query) ||
        code.description?.toLowerCase().includes(query) ||
        code.author?.toLowerCase().includes(query) ||
        code.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }

  if (selectedTags && selectedTags.length > 0) {
    filtered = filtered.filter(code =>
      selectedTags.some(selectedTag =>
        code.tags?.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
      )
    );
  }

  return filtered;
};

/** Sorted unique tags across all entries. */
export const getUniqueTags = codes => {
  const tagsSet = new Set();
  codes.forEach(code => {
    if (Array.isArray(code.tags)) code.tags.forEach(tag => tagsSet.add(tag));
  });
  return Array.from(tagsSet).sort();
};

const githubService = {
  fetchGithubUserProfile,
  fetchGithubCodes,
  createGithubCode,
  updateGithubCode,
  deleteGithubCode,
  validateGithubData,
  formatGithubCode,
  filterGithubCodes,
  getUniqueTags,
};

export default githubService;
